// src/components/Topology/TopologyView.jsx
import React from 'react';
import { useStore } from '../../store/useStore';

export default function TopologyView() {
  const alerts = useStore(s => s.alerts);
  const criticalSources = new Set(
    alerts.filter(a => a.severity === 'CRITICAL' && a.status === 'OPEN').map(a => a.source)
  );

  const nodeColor  = (src) => criticalSources.has(src) ? '#ef4444' : '#22c55e';
  const nodeStroke = (src) => criticalSources.has(src) ? 'rgba(239,68,68,.5)' : 'rgba(34,197,94,.4)';

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="text-sm font-semibold mb-3">🌐 Service Topology Map</div>

      <div className="flex-1 rounded-xl overflow-hidden relative"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
        <div className="scan-line" />
        <svg width="100%" height="100%" viewBox="0 0 900 480" preserveAspectRatio="xMidYMid meet">
          <defs>
            <marker id="ar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round"/>
            </marker>
          </defs>

          {/* GCP cluster */}
          <rect x="30" y="30" width="200" height="200" rx="12" fill="rgba(66,133,244,.05)" stroke="rgba(66,133,244,.3)" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="130" y="52" textAnchor="middle" fill="#4285f4" fontSize="10" fontFamily="Inter" fontWeight="600" letterSpacing="1.5">GCP</text>
          <ServiceBox x={40}  y={60}  w={180} h={44} label="Cloud Run"  sub="api-gateway" color="#4285f4" warn={criticalSources.has('GCP')} />
          <ServiceBox x={40}  y={112} w={180} h={44} label="BigQuery"   sub="analytics"   color="#4285f4" />
          <ServiceBox x={40}  y={164} w={180} h={44} label="Pub/Sub"    sub="orders-sub"  color="#4285f4" />

          {/* Azure cluster */}
          <rect x="670" y="30" width="200" height="200" rx="12" fill="rgba(0,120,212,.05)" stroke="rgba(0,120,212,.3)" strokeWidth="1" strokeDasharray="5 3"/>
          <text x="770" y="52" textAnchor="middle" fill="#0078d4" fontSize="10" fontFamily="Inter" fontWeight="600" letterSpacing="1.5">AZURE</text>
          <ServiceBox x={680} y={60}  w={180} h={44} label="AKS"        sub="nodepool1"     color="#0078d4" warn={criticalSources.has('AZURE')} />
          <ServiceBox x={680} y={112} w={180} h={44} label="Azure SQL"  sub="prod-db-east"  color="#0078d4" />
          <ServiceBox x={680} y={164} w={180} h={44} label="Key Vault"  sub="prod-kv"       color="#0078d4" />

          {/* AI Observer center */}
          <rect x="360" y="150" width="180" height="64" rx="14" fill="rgba(139,92,246,.12)" stroke="rgba(139,92,246,.5)" strokeWidth="1.5"/>
          <text x="450" y="176" textAnchor="middle" fill="#c4b5fd" fontSize="12" fontFamily="Inter" fontWeight="600">🤖 AI Observer</text>
          <text x="450" y="196" textAnchor="middle" fill="#a78bfa" fontSize="9" fontFamily="Space Mono">correlating 4 sources</text>

          {/* MongoDB */}
          <ServiceBox x={200} y={320} w={180} h={56} label="MongoDB Atlas" sub="rs0 — 3-node replica" color="#22c55e" warn={criticalSources.has('MONGODB')} />

          {/* PostgreSQL */}
          <ServiceBox x={520} y={320} w={180} h={56} label="PostgreSQL" sub="prod-pg-01 primary" color="#a78bfa" warn={criticalSources.has('DATABASE')} />

          {/* Connections */}
          <line x1="230" y1="130" x2="360" y2="180" stroke="rgba(66,133,244,.5)"  strokeWidth="1" markerEnd="url(#ar)"/>
          <line x1="670" y1="130" x2="540" y2="180" stroke="rgba(0,120,212,.5)"   strokeWidth="1" markerEnd="url(#ar)"/>
          <line x1="450" y1="214" x2="295" y2="320" stroke="rgba(139,92,246,.6)"  strokeWidth="1.5" markerEnd="url(#ar)"/>
          <line x1="450" y1="214" x2="610" y2="320" stroke="rgba(139,92,246,.6)"  strokeWidth="1.5" markerEnd="url(#ar)"/>
          <line x1="230" y1="180" x2="200" y2="342" stroke="rgba(66,133,244,.2)"  strokeWidth=".8" strokeDasharray="4 3"/>
          <line x1="670" y1="150" x2="700" y2="320" stroke="rgba(0,120,212,.2)"   strokeWidth=".8" strokeDasharray="4 3"/>
          <line x1="380" y1="348" x2="520" y2="348" stroke="rgba(255,255,255,.15)" strokeWidth="1" markerEnd="url(#ar)"/>
        </svg>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mt-3 text-xs" style={{ color: 'var(--text-3)' }}>
        <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"/> Healthy</div>
        <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"/>   Critical</div>
        <div className="flex items-center gap-1">
          <svg width="20" height="4"><line x1="0" y1="2" x2="20" y2="2" stroke="#8b5cf6" strokeWidth="1.5"/></svg>
          AI correlation
        </div>
        <div className="flex items-center gap-1">
          <svg width="20" height="4"><line x1="0" y1="2" x2="20" y2="2" stroke="#64748b" strokeWidth="1" strokeDasharray="3 2"/></svg>
          Data flow
        </div>
      </div>
    </div>
  );
}

function ServiceBox({ x, y, w, h, label, sub, color, warn }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="8"
        fill={warn ? 'rgba(239,68,68,.12)' : `${color}1a`}
        stroke={warn ? 'rgba(239,68,68,.5)' : `${color}60`}
        strokeWidth={warn ? 1.5 : 0.8}/>
      <text x={x + w / 2} y={y + h / 2 - 6} textAnchor="middle"
        fill={warn ? '#f87171' : '#e2e8f0'} fontSize="11" fontFamily="Inter" fontWeight="500">{label}</text>
      <text x={x + w / 2} y={y + h / 2 + 10} textAnchor="middle"
        fill={warn ? '#fca5a5' : color} fontSize="9" fontFamily="Space Mono">
        {warn ? '⚠ ' : ''}{sub}
      </text>
    </g>
  );
}
